import { vocab } from '@/ds';
import { formatTimestamp, toInstant } from '@/lib/format/format';
import type { CandidateListItem } from '@/features/candidates/api';
import type { CandidatesSearch } from '@/features/candidates/search';
import {
  candidateStatus,
  isBlocking,
  isChangedPublished,
  recordWarnings,
  WARNING_FILTERS,
  type CandidateWarning,
  type WarningFilter,
} from '@/features/candidates/vocab';
import type { DuplicateGroupItem } from '@/features/duplicates/api';
import type { Automation } from '@/features/automation/api';
import type { ProviderSpend, Run, Spend } from '@/features/runs/api';
import type { Rule } from '@/features/rules/api';
import { sourceLabel, type Source, type SourceHealth } from '@/features/sources/api';
import { andList } from '@/features/automation/vocab';
import { isImport, isLive, isRunning } from '@/features/runs/vocab';
import { CAP_REMEDY, healthOf, healthStatus, isActive } from '@/features/sources/vocab';

// Most severe first: what blocks, then what fails, then what needs a look; a plain pending record
// last, with its rule's suggestion or with none.
export const REASON_ORDER = [
  'blocking-warning',
  'ai-account-refused',
  'source-failing',
  'source-overdue',
  'source-silent',
  'spend-alert',
  'spend-unrecorded',
  'cancelled-at-source',
  'postponed-at-source',
  'changed-published',
  'duplicate-group',
  'possibly-cancelled',
  'source-changed',
  'source-capped',
  'source-slipping',
  'low-confidence',
  'suggestion',
  'no-rule',
] as const;

export type InboxReason = (typeof REASON_ORDER)[number];
type SourceReason =
  'source-failing' | 'source-overdue' | 'source-silent' | 'source-capped' | 'source-slipping';
type CandidateReason = Exclude<
  InboxReason,
  | 'source-failing'
  | 'source-overdue'
  | 'source-silent'
  | 'source-capped'
  | 'source-slipping'
  | 'spend-alert'
  | 'spend-unrecorded'
  | 'ai-account-refused'
  | 'duplicate-group'
>;

interface Entry<K extends string, R extends InboxReason> {
  kind: K;
  key: string;
  reason: R;
  /** Since when it has waited, for the item's age and the order within a reason. */
  since: string;
}

export type InboxEntry =
  | (Entry<'candidate', CandidateReason> & {
      candidate: CandidateListItem;
      /** The candidates view that holds this record for this reason. */
      view: Partial<CandidatesSearch>;
    })
  | (Entry<'group', 'duplicate-group'> & { group: DuplicateGroupItem })
  | (Entry<'source', SourceReason> & {
      source: Source;
      health: SourceHealth;
      /** The run the API's verdict read; an overdue source that never ran has none. */
      run?: NonNullable<SourceHealth['lastRun']>;
    })
  | (Entry<'spend', 'spend-alert' | 'spend-unrecorded'> & {
      /** One provider's reading, and the summary it came from for the window. */
      reading: ProviderSpend;
      spend: Spend;
    })
  | (Entry<'account', 'ai-account-refused'> & {
      /** The provider in use, and the finished run whose calls it didn't accept. */
      provider: string;
      run: Run;
    });

export type SourceEntry = Extract<InboxEntry, { kind: 'source' }>;

export interface Inbox {
  entries: InboxEntry[];
  /** Open groups past the ones read: counted, and linked rather than listed. */
  moreGroups: number;
  /** Everything waiting on a person: the entries and the groups past them. */
  count: number;
  tone?: 'caution' | 'critical';
}

// The record's own warnings as reasons. Duplicate suspected stands for its group, which is listed.
const BY_WARNING: Partial<Record<CandidateWarning, CandidateReason | 'duplicate-group'>> = {
  'changed-published': 'changed-published',
  'duplicate-suspected': 'duplicate-group',
  'possibly-cancelled': 'possibly-cancelled',
  'cancelled-at-source': 'cancelled-at-source',
  'postponed-at-source': 'postponed-at-source',
  'source-changed': 'source-changed',
  'low-confidence': 'low-confidence',
};

const rank = (reason: InboxReason) => REASON_ORDER.indexOf(reason);

const isWarningFilter = (warning: CandidateWarning): warning is WarningFilter =>
  (WARNING_FILTERS as readonly string[]).includes(warning);

type CandidateEntry = Extract<InboxEntry, { kind: 'candidate' }>;

function toCandidateEntry(
  candidate: CandidateListItem,
  reason: CandidateReason,
  since: string,
  view: Partial<CandidatesSearch>,
): CandidateEntry {
  return { kind: 'candidate', key: `candidate:${candidate.id}`, reason, since, candidate, view };
}

function viewFor(
  candidate: CandidateListItem,
  { warning, reason }: { warning: CandidateWarning; reason: InboxReason },
): Partial<CandidatesSearch> {
  if (isChangedPublished(candidate)) return { changed: true };
  if (reason === 'possibly-cancelled') return { cancelled: true };
  if (isWarningFilter(warning)) return { warning };
  return {};
}

function candidateEntry(candidate: CandidateListItem): InboxEntry | undefined {
  const [top] = recordWarnings(candidate)
    .flatMap((warning) => {
      const reason = isBlocking(warning) ? 'blocking-warning' : BY_WARNING[warning];
      return reason ? [{ warning, reason }] : [];
    })
    .sort((a, b) => rank(a.reason) - rank(b.reason));
  if (!top) {
    const reason = candidate.suggestion ? 'suggestion' : 'no-rule';
    return toCandidateEntry(candidate, reason, candidate.lastChangedAt, {});
  }
  if (top.reason === 'duplicate-group') return undefined;
  return toCandidateEntry(candidate, top.reason, candidate.lastChangedAt, viewFor(candidate, top));
}

/** A decided record the pipeline can't find any more: its event is live, and a person decides. */
function flaggedEntry(candidate: CandidateListItem): InboxEntry {
  return toCandidateEntry(candidate, 'possibly-cancelled', candidate.lastSeenAt, {
    status: candidateStatus(candidate),
    cancelled: true,
  });
}

export interface ItemFace {
  action: 'Compare' | 'Review';
  /** Whether the item shows what the live event's change would do. */
  showsChange: boolean;
  /** The record's warnings its reason doesn't already say. */
  chips: CandidateWarning[];
}

/**
 * How a record's item looks: a live event's change compares and shows what it changes, whatever its
 * reason. A blocking reason comes from no single warning, so it keeps every chip.
 */
export function itemFace(entry: CandidateEntry): ItemFace {
  const changed = isChangedPublished(entry.candidate);
  const chips =
    entry.reason === 'changed-published'
      ? []
      : recordWarnings(entry.candidate).filter(
          (warning) =>
            warning !== 'changed-published' &&
            (entry.reason === 'blocking-warning' || BY_WARNING[warning] !== entry.reason),
        );
  return { action: changed ? 'Compare' : 'Review', showsChange: changed, chips };
}

const SOURCE_REASONS: Partial<Record<string, SourceReason>> = {
  failing: 'source-failing',
  overdue: 'source-overdue',
  silent: 'source-silent',
  capped: 'source-capped',
  slipping: 'source-slipping',
};

/** The API's verdict: Failing, Overdue, Returned nothing, Capped or Slipping. Paused and disabled sources have none. */
function sourceEntry(source: Source, now: Date): InboxEntry | undefined {
  const health = healthOf(source);
  const reason = SOURCE_REASONS[healthStatus(source) ?? ''];
  if (!health || !reason) return;
  const run = health.lastRun ?? undefined;
  // An overdue source must stay in view even if the API sent no due time.
  const since =
    reason === 'source-overdue'
      ? (health.overdue?.dueAt ?? run?.finishedAt ?? now.toISOString())
      : run?.finishedAt;
  if (!since) return;
  return { kind: 'source', key: `source:${source.id}`, reason, since, source, health, run };
}

const sentence = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** An item's meta line: the API's evidence, its time worded here; a capped source adds its remedy. */
export function sourceWords(entry: SourceEntry, now?: Date): string {
  if (entry.reason === 'source-overdue' && entry.health.overdue) {
    return `Due since ${formatTimestamp(entry.since, now)}`;
  }
  const reason = sentence(entry.health.reason);
  return entry.reason === 'source-capped' ? `${reason}. ${CAP_REMEDY}` : reason;
}

export type SourceAction =
  | { kind: 'run-now' }
  | { kind: 'pause' }
  | { kind: 'open-run'; runId: string }
  | { kind: 'open-source' };

/**
 * What an item offers. Pausing an overdue source would hide the signal, a capped source's fix is
 * the API's configuration, and a slipping source's trend is on its page.
 */
export function sourceActions(entry: SourceEntry): SourceAction[] {
  const openRun: SourceAction[] = entry.run ? [{ kind: 'open-run', runId: entry.run.runId }] : [];
  switch (entry.reason) {
    case 'source-overdue':
      return [{ kind: 'run-now' }, { kind: 'open-source' }];
    case 'source-slipping':
      return [{ kind: 'open-source' }];
    case 'source-capped':
      return openRun;
    case 'source-failing':
    case 'source-silent':
      return [{ kind: 'pause' }, ...openRun];
  }
}

/** The API's own verdicts for each provider: past the alert (the ceiling is past it too), or calls that cost nothing. */
function spendEntries(spend: Spend): InboxEntry[] {
  return spend.providers.flatMap((reading): InboxEntry[] => {
    const reason =
      reading.overAlertThreshold || reading.ceilingReached
        ? 'spend-alert'
        : reading.pricingUnconfigured
          ? 'spend-unrecorded'
          : undefined;
    if (!reason) return [];
    return [
      {
        kind: 'spend',
        key: `spend:${reading.provider}`,
        reason,
        since: spend.windowEnd,
        spend,
        reading,
      },
    ];
  });
}

/**
 * The provider in use when its newest finished run that called it had calls the provider didn't accept.
 * A later such run with none clears it; so does another provider coming into use, and so does a
 * switch, until a run starts after it.
 */
function accountEntries(runs: readonly Run[], spend: Spend, chosenAt: string | null): InboxEntry[] {
  const provider = spend.activeProvider;
  const chosen = chosenAt ? toInstant(chosenAt).getTime() : -Infinity;
  const run = runs.find(
    (r) =>
      r.llmProvider === provider &&
      !isRunning(r) &&
      r.llmCalls > 0 &&
      toInstant(r.startedAt).getTime() >= chosen,
  );
  if (!run || run.accountFailures <= 0) return [];
  return [
    {
      kind: 'account',
      key: `account:${provider}`,
      reason: 'ai-account-refused',
      since: run.finishedAt ?? run.startedAt,
      provider,
      run,
    },
  ];
}

// Of two spend entries that wait alike, the provider in use comes first.
const spendRank = (e: InboxEntry) => (e.kind === 'spend' && !e.reading.isActive ? 1 : 0);

const byReasonThenAge = (a: InboxEntry, b: InboxEntry) =>
  rank(a.reason) - rank(b.reason) ||
  toInstant(a.since).getTime() - toInstant(b.since).getTime() ||
  spendRank(a) - spendRank(b) ||
  a.key.localeCompare(b.key);

/**
 * The Inbox from what's loaded; a part still loading or failed adds nothing. Each record, group
 * or source is one entry with its most severe reason, most severe first and oldest first within.
 * A flagged decided record is always Possibly cancelled, whatever else it carries.
 */
export function assembleInbox({
  pending,
  flagged,
  groups,
  sources,
  spend,
  runs,
  modelChosenAt,
  now = new Date(),
}: {
  pending?: readonly CandidateListItem[];
  flagged?: readonly CandidateListItem[];
  groups?: { items: readonly DuplicateGroupItem[]; totalCount: number };
  sources?: readonly Source[];
  spend?: Spend;
  /** The latest runs, newest first. */
  runs?: readonly Run[];
  /** When the AI model was last chosen (null: never); undefined until the model options are read. */
  modelChosenAt?: string | null;
  now?: Date;
}): Inbox {
  const entries: InboxEntry[] = [
    ...(pending ?? []).flatMap((candidate) => candidateEntry(candidate) ?? []),
    ...(flagged ?? []).map(flaggedEntry),
    ...(groups?.items ?? []).map((group): InboxEntry => ({
      kind: 'group',
      key: `group:${group.id}`,
      reason: 'duplicate-group',
      since: group.createdAt,
      group,
    })),
    ...(sources ?? []).flatMap((source) => sourceEntry(source, now) ?? []),
    ...(spend ? spendEntries(spend) : []),
    ...(spend && runs && modelChosenAt !== undefined ? accountEntries(runs, spend, modelChosenAt) : []),
  ].sort(byReasonThenAge);

  const moreGroups = groups ? Math.max(0, groups.totalCount - groups.items.length) : 0;
  const count = entries.length + moreGroups;
  const tones = new Set(entries.map((e) => vocab.REASONS[e.reason]?.tone));
  return {
    entries,
    moreGroups,
    count,
    // Plain records waiting for a person are the review queue, not an alarm: they keep the count calm.
    tone: tones.has('critical')
      ? 'critical'
      : tones.has('caution') || moreGroups > 0
        ? 'caution'
        : undefined,
  };
}

/**
 * The all-clear's claim about sources: that every active one works only when each reads Healthy,
 * else the retrying and not-yet-run ones by name; none when no source is active.
 */
export function allClearSources(sources: readonly Source[], now = new Date()): string | undefined {
  const active = sources.filter(isActive);
  if (active.length === 0) return undefined;
  if (active.every((s) => healthStatus(s) === 'healthy')) return 'Every active source is working';

  const name = (s: Source) => sourceLabel(s) ?? s.name;
  const byTime = new Map<string, string[]>();
  for (const s of active) {
    const retry = healthStatus(s) === 'retrying' ? healthOf(s)?.retry : undefined;
    if (!retry) continue;
    const when = toInstant(retry.dueAt) <= now ? 'now' : `at ${formatTimestamp(retry.dueAt, now)}`;
    byTime.set(when, [...(byTime.get(when) ?? []), name(s)]);
  }
  const retrying = [...byTime].map(
    ([when, names]) =>
      `${andList(names)} ${names.length === 1 ? 'is' : 'are'} retrying ${when} after a run that fetched nothing`,
  );
  const unrun = active.filter((s) => healthStatus(s) === 'not-run').map(name);
  const parts = unrun.length
    ? [...retrying, `${andList(unrun)} ${unrun.length === 1 ? "hasn't" : "haven't"} run yet`]
    : retrying;
  if (parts.length === 0) return undefined;
  return parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')}, and ${parts.at(-1)}`;
}

export interface LeftForNextRun {
  key: string;
  rule: Rule;
  source: Source;
  run: Run;
  decided: number;
  left: number;
}

/**
 * Work a rule in Act left on a source's newest finished scrape run, while the next run will take it
 * up: the rule still exists with records to cover, automation is on and the source is enabled and
 * not paused. A run in progress, or finished with its rules still deciding, is skipped. Imports belong to no source.
 */
export function leftForNextRun({
  runs,
  sources,
  automation,
  rules,
}: {
  runs?: readonly Run[];
  sources?: readonly Source[];
  automation?: Automation;
  rules?: readonly Rule[];
}): LeftForNextRun[] {
  if (!runs || !sources || !rules || !automation || automation.paused) return [];
  const sourceById = new Map(sources.map((s) => [s.id, s]));
  const ruleById = new Map(rules.map((r) => [r.id, r]));
  const newest = new Map<string, Run>();
  for (const run of runs) {
    if (run.sourceId && !isImport(run) && !isLive(run) && !newest.has(run.sourceId)) {
      newest.set(run.sourceId, run);
    }
  }
  return [...newest.values()].flatMap((run) => {
    const source = sourceById.get(run.sourceId ?? '');
    if (!source || !isActive(source)) return [];
    return run.ruleFigures.flatMap((figure) => {
      const rule = ruleById.get(figure.ruleId);
      if (!rule || rule.mode !== 'Act' || rule.pendingMatches <= 0 || figure.left <= 0) return [];
      return [
        {
          key: `${run.runId}:${rule.id}`,
          rule,
          source,
          run,
          decided: figure.decided,
          left: figure.left,
        },
      ];
    });
  });
}
